/**
 * useNarration — lecture narrée d'une scène + progression (pour le karaoké),
 * avec dégradation gracieuse et vraie pause/reprise (conserve la position).
 *
 * Chaîne de repli pour une scène :
 *   1. `audioSrc` fourni ET son non coupé → lecture du fichier audio (ElevenLabs).
 *   2. échec audio, pas d'`audioSrc`, OU son coupé → synthèse vocale de l'appareil
 *      (si voix dispo et son non coupé).
 *   3. sinon → repli minuté (`durationMs`) : le sous-titre défile sans voix.
 *
 * Dans tous les cas :
 *   - `progress` (0→1) avance pendant la lecture (piloté par requestAnimationFrame) ;
 *     il alimente le surlignage karaoké et la barre de progression.
 *   - `onEnd` est appelé UNE SEULE FOIS à la fin (sauf interruption via `stop`).
 *   - `pause()` fige la position (audio en pause, voix/minuteur suspendus) ;
 *     `resume()` reprend là où on s'était arrêté — SANS redémarrer la scène.
 *   - `usingTimerFallback` passe à vrai quand, son actif, ni l'audio ni la voix
 *     ne sont disponibles (on tombe sur le repli minuté silencieux) → sert à
 *     afficher « Voix indisponible… ».
 *
 * La lecture doit démarrer sur une interaction utilisateur (ouverture au tap),
 * conformément aux politiques d'autoplay des navigateurs.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const isBrowser = typeof window !== 'undefined';
const synth = isBrowser && 'speechSynthesis' in window ? window.speechSynthesis : null;

// Estimation de durée de parole quand on n'a pas de piste audio (repère : ~2,7 mots/s).
function estimateSpeechMs(text, floorMs) {
  const words = (text || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(floorMs || 0, 800 + words * 360);
}

export function useNarration() {
  const supported = !!synth;
  const [speaking, setSpeaking] = useState(false);
  const [hasVoices, setHasVoices] = useState(false);
  const [progress, setProgress] = useState(0);
  const [usingTimerFallback, setUsingTimerFallback] = useState(false);

  const audioRef = useRef(null);
  const utterRef = useRef(null);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const tokenRef = useRef(0);
  const voicesRef = useRef([]);
  const pausedRef = useRef(false);
  // Fonctions de pause/reprise propres à la lecture en cours (audio vs voix/minuteur).
  const pauseFnRef = useRef(null);
  const resumeFnRef = useRef(null);

  // Charge la liste des voix (asynchrone sur Chrome/Android : voiceschanged).
  useEffect(() => {
    if (!supported) return undefined;
    const load = () => {
      voicesRef.current = synth.getVoices() || [];
      setHasVoices(voicesRef.current.length > 0);
    };
    load();
    synth.addEventListener?.('voiceschanged', load);
    return () => synth.removeEventListener?.('voiceschanged', load);
  }, [supported]);

  const pickVoice = useCallback((langCode) => {
    const voices = voicesRef.current;
    if (!voices || !voices.length) return null;
    const lc = (langCode || 'fr-FR').toLowerCase();
    const base = lc.split('-')[0];
    return (
      voices.find((v) => v.lang && v.lang.toLowerCase() === lc)
      || voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(base))
      || null
    );
  }, []);

  const cancelRaf = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Interrompt toute lecture en cours et invalide les callbacks en attente.
  const stop = useCallback(() => {
    tokenRef.current += 1;
    cancelRaf();
    clearTimer();
    pausedRef.current = false;
    pauseFnRef.current = null;
    resumeFnRef.current = null;
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      try { audioRef.current.pause(); } catch { /* noop */ }
      audioRef.current = null;
    }
    if (synth) {
      try { synth.cancel(); } catch { /* noop */ }
    }
    utterRef.current = null;
    setSpeaking(false);
    setProgress(0);
    setUsingTimerFallback(false);
  }, []);

  /**
   * Joue une scène. Appelle onEnd exactement une fois à la fin (hors interruption).
   * @param {{ text?:string, langCode?:string, audioSrc?:string|null, audioStartSec?:number, audioEndSec?:number, durationMs?:number }} scene
   * @param {{ onEnd?: Function, muted?: boolean }} [opts]
   */
  const play = useCallback((scene, opts = {}) => {
    stop(); // repart proprement + incrémente le token
    const token = tokenRef.current;
    const { onEnd, muted = false } = opts;
    const {
      text,
      langCode,
      audioSrc,
      audioStartSec = 0,
      audioEndSec,
      durationMs = 5000,
    } = scene || {};
    const segStart = Math.max(0, audioStartSec || 0);
    const segEnd = audioEndSec != null && audioEndSec > segStart ? audioEndSec : null;
    const isSegment = segEnd != null;

    // Une fin qui survient PENDANT une pause est différée jusqu'à la reprise, pour
    // que la pause fige réellement la progression — y compris quand la source est
    // flaky (la voix synthétique de Chrome peut émettre `onend` malgré `pause()`).
    let endedWhilePaused = false;
    const finish = () => {
      if (token !== tokenRef.current) return;
      if (pausedRef.current) {
        endedWhilePaused = true;
        return;
      }
      tokenRef.current += 1;
      cancelRaf();
      clearTimer();
      pausedRef.current = false;
      pauseFnRef.current = null;
      resumeFnRef.current = null;
      setProgress(1);
      setSpeaking(false);
      onEnd?.();
    };

    // Progression minutée (voix synthétique ou repli), fondée sur une échéance
    // pour pouvoir être suspendue puis reprise sans repartir de zéro.
    const timed = { total: 0, deadline: 0, remaining: 0 };
    const runTimed = () => {
      cancelRaf();
      const tick = () => {
        if (token !== tokenRef.current) return;
        const remaining = timed.deadline - performance.now();
        const p = Math.min(1, Math.max(0, 1 - remaining / timed.total));
        setProgress(p);
        if (remaining > 0) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };
    const armFinish = (ms) => {
      clearTimer();
      timerRef.current = setTimeout(finish, ms);
    };
    const startTimed = (totalMs) => {
      timed.total = totalMs;
      timed.deadline = performance.now() + totalMs;
      runTimed();
      armFinish(totalMs);
    };
    const pauseTimed = () => {
      cancelRaf();
      clearTimer();
      timed.remaining = Math.max(0, timed.deadline - performance.now());
      setSpeaking(false);
    };
    const resumeTimed = () => {
      timed.deadline = performance.now() + timed.remaining;
      runTimed();
      armFinish(timed.remaining);
      setSpeaking(true);
    };

    // Progression pilotée par la lecture audio réelle (reprend depuis currentTime).
    const trackAudioProgress = (audio) => {
      cancelRaf();
      const segDurationSec = () => {
        if (isSegment) return segEnd - segStart;
        return audio.duration > 0 ? audio.duration : durationMs / 1000;
      };
      const tick = () => {
        if (token !== tokenRef.current) return;
        const dur = segDurationSec();
        if (dur > 0) {
          const pos = audio.currentTime - segStart;
          setProgress(Math.min(1, Math.max(0, pos / dur)));
          if (isSegment && audio.currentTime >= segEnd - 0.02) {
            try { audio.pause(); } catch { /* noop */ }
            finish();
            return;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    // Voix de l'appareil, sinon repli minuté.
    const speakOrTimer = () => {
      if (!muted && supported && text && voicesRef.current.length > 0) {
        try {
          const utter = new SpeechSynthesisUtterance(text);
          utter.lang = langCode || 'fr-FR';
          const voice = pickVoice(langCode);
          if (voice) utter.voice = voice;
          utter.rate = 0.98;
          utter.pitch = 1;
          utter.onend = finish;
          // Échec de la voix → repli minuté ; on coupe d'abord la boucle rAF en
          // cours pour ne pas avoir deux sources de progression concurrentes.
          utter.onerror = () => {
            cancelRaf();
            startTimed(durationMs);
            setUsingTimerFallback(true);
            pauseFnRef.current = pauseTimed;
            resumeFnRef.current = resumeTimed;
          };
          utterRef.current = utter;
          const estMs = estimateSpeechMs(text, durationMs);
          timed.total = estMs + 2500; // marge : la fin réelle vient de onend
          timed.deadline = performance.now() + timed.total;
          runTimed();
          synth.cancel();
          synth.speak(utter);
          // Filet : certains navigateurs n'émettent pas onend de façon fiable.
          armFinish(estMs + 3000);
          setUsingTimerFallback(false);
          pauseFnRef.current = () => {
            pauseTimed();
            try { synth.pause(); } catch { /* noop */ }
          };
          resumeFnRef.current = () => {
            if (endedWhilePaused) { finish(); return; }
            try { synth.resume(); } catch { /* noop */ }
            resumeTimed();
          };
          return;
        } catch {
          // bascule vers le repli minuté ci-dessous
        }
      }
      startTimed(durationMs);
      // Repli minuté SANS voix : ne le signale comme « voix indisponible » que si
      // ce n'est pas un choix de l'utilisateur (son coupé).
      setUsingTimerFallback(!muted);
      pauseFnRef.current = pauseTimed;
      resumeFnRef.current = resumeTimed;
    };

    setSpeaking(true);
    setProgress(0);
    pausedRef.current = false;

    // 1) Fichier audio enregistré — ignoré si le son est coupé.
    if (audioSrc && !muted) {
      const audio = new Audio(audioSrc);
      audioRef.current = audio;
      if (!isSegment) audio.onended = finish;
      audio.onerror = () => {
        if (token !== tokenRef.current) return;
        audioRef.current = null;
        cancelRaf();
        speakOrTimer();
      };

      const beginAudio = () => {
        if (token !== tokenRef.current) return;
        if (isSegment) {
          try { audio.currentTime = segStart; } catch { /* noop */ }
        }
        trackAudioProgress(audio);
        const p = audio.play();
        if (p && typeof p.catch === 'function') {
          p.catch((err) => {
            if (err && err.name === 'AbortError') return;
            if (token !== tokenRef.current) return;
            audioRef.current = null;
            cancelRaf();
            speakOrTimer();
          });
        }
      };

      if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
        beginAudio();
      } else {
        audio.addEventListener('loadedmetadata', beginAudio, { once: true });
      }

      setUsingTimerFallback(false);
      pauseFnRef.current = () => {
        cancelRaf();
        try { audio.pause(); } catch { /* noop */ }
        setSpeaking(false);
      };
      resumeFnRef.current = () => {
        if (endedWhilePaused) { finish(); return; }
        const pr = audio.play();
        if (pr && typeof pr.catch === 'function') pr.catch(() => { /* autoplay bloqué */ });
        trackAudioProgress(audio);
        setSpeaking(true);
      };
      return;
    }

    // 2/3) Voix de l'appareil, sinon repli minuté.
    speakOrTimer();
  }, [pickVoice, stop, supported]);

  // Pause réelle : fige la position sans réinitialiser la progression ni le token.
  const pause = useCallback(() => {
    if (pausedRef.current) return;
    pausedRef.current = true;
    if (pauseFnRef.current) pauseFnRef.current();
    else setSpeaking(false);
  }, []);

  // Reprise : continue depuis la position sauvegardée (audio / voix / minuteur).
  const resume = useCallback(() => {
    if (!pausedRef.current) return;
    pausedRef.current = false;
    if (resumeFnRef.current) resumeFnRef.current();
    else setSpeaking(true);
  }, []);

  // Nettoyage au démontage.
  useEffect(() => stop, [stop]);

  return {
    play,
    stop,
    pause,
    resume,
    speaking,
    progress,
    /** Repli minuté silencieux actif (aucune voix disponible, son non coupé). */
    usingTimerFallback,
    /** La voix de l'appareil est réellement disponible pour narrer. */
    ttsAvailable: supported && hasVoices,
  };
}

export default useNarration;
